import React from 'react';
import classNames from 'classnames';
import ContentLoader, { IContentLoaderProps } from 'react-content-loader';

import './SelectRowsPlaceholder.styles.scss';

const COMMON: Partial<IContentLoaderProps> = {
  backgroundColor: '#e7f1fc',
  foregroundColor: '#ffffff',
};

const NAME_VIEWBOX_WIDTH = 300;

// Slightly different name lengths so the skeleton reads as a list of people
// instead of a block of identical bars.
const NAME_WIDTH_FACTORS = [0.6, 0.45, 0.7, 0.5, 0.65, 0.4];

type PlaceholderRowProps = {
  nameWidthFactor: number;
  trailingWidths: number[];
};

const PlaceholderRow: React.FC<PlaceholderRowProps> = (props) => {
  const { nameWidthFactor, trailingWidths } = props;

  return (
    <div className="select-rows-placeholder__row">
      <ContentLoader
        className="select-rows-placeholder__checkbox"
        viewBox="0 0 20 20"
        {...COMMON}
      >
        <rect x="0" y="0" rx="4" ry="4" width="20" height="20" />
      </ContentLoader>

      <ContentLoader
        className="select-rows-placeholder__name"
        viewBox={`0 0 ${NAME_VIEWBOX_WIDTH} 16`}
        preserveAspectRatio="none"
        {...COMMON}
      >
        <rect
          x="0"
          y="0"
          rx="2"
          ry="2"
          width={NAME_VIEWBOX_WIDTH * nameWidthFactor}
          height="16"
        />
      </ContentLoader>

      {trailingWidths.map((width, index) => (
        <ContentLoader
          key={index}
          className="select-rows-placeholder__trailing"
          style={{ width }}
          // The viewBox matches the column width so the bar keeps its size
          // while staying centered, like the real column content.
          viewBox={`0 0 ${width} 16`}
          {...COMMON}
        >
          <rect
            x={(width - Math.min(width, 64)) / 2}
            y="0"
            rx="2"
            ry="2"
            width={Math.min(width, 64)}
            height="16"
          />
        </ContentLoader>
      ))}
    </div>
  );
};

type SelectRowsPlaceholderProps = {
  className?: string;
  /** Number of skeleton rows rendered below the header row. */
  rows?: number;
  /** Widths of the columns rendered after the name, matching the real list. */
  trailingWidths?: number[];
  /** Renders the "x displayed items" line above the list. */
  hasCount?: boolean;
};

const SelectRowsPlaceholder: React.FC<SelectRowsPlaceholderProps> = (props) => {
  const { className, rows = 6, trailingWidths = [], hasCount = false } = props;

  const classes = classNames('select-rows-placeholder', className);

  return (
    <div className={classes}>
      {hasCount && (
        <ContentLoader
          className="select-rows-placeholder__count"
          viewBox="0 0 120 12"
          {...COMMON}
        >
          <rect x="0" y="0" rx="2" ry="2" width="120" height="12" />
        </ContentLoader>
      )}

      <PlaceholderRow nameWidthFactor={0.25} trailingWidths={trailingWidths} />

      <div className="select-rows-placeholder__rows">
        {Array.from({ length: rows }).map((_, index) => (
          <PlaceholderRow
            key={index}
            nameWidthFactor={
              NAME_WIDTH_FACTORS[index % NAME_WIDTH_FACTORS.length]
            }
            trailingWidths={trailingWidths}
          />
        ))}
      </div>
    </div>
  );
};

export default SelectRowsPlaceholder;
